import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Sheet,
  IconButton,
  Modal,
  ModalDialog,
  ModalClose,
  AspectRatio,
  List,
  ListItem,
  Input,
  Divider,
} from '@mui/joy';
import { Download, X } from 'lucide-react';
import { authFetch } from '../../utils/authFetch';

const IMAGES_PER_PAGE = 10;
const anniDisponibili = [2026, 2025, 2024, 2023];

// ✅ timestamp sempre prima dell'estensione (ultime 13 cifre prima di .jpg/.png/...)
function extractTimestampFromEnd(filename) {
  // match: ...<non cifra><13 cifre>.<estensione>
  // esempi ok:
  // 1751707649594-xxxx.jpg
  // image_picker_xxx-1752899063956.jpg
  // SEM1-1751797959376.png
  const match = filename.match(/(\d{13})\.[a-zA-Z0-9]+$/);
  return match ? Number(match[1]) : null;
}

function getYearMonthLabel(timestamp) {
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = date.toLocaleString('it-IT', { month: 'long' });
  return {
    year,
    month: month.charAt(0).toUpperCase() + month.slice(1),
    label: `${year}-${String(date.getMonth() + 1).padStart(2, '0')}`, // YYYY-MM
    sortKey: year * 100 + (date.getMonth() + 1),
  };
}

function groupFilesByMonth(files) {
  const grouped = {};

  files.forEach((file) => {
    const ts = extractTimestampFromEnd(file);
    if (!ts) return;

    const { label, year, month, sortKey } = getYearMonthLabel(ts);
    if (!grouped[label]) grouped[label] = { year, month, sortKey, files: [] };
    grouped[label].files.push(file);
  });

  // Ordina mesi dal più recente al meno recente
  const orderedLabels = Object.values(grouped)
    .sort((a, b) => b.sortKey - a.sortKey)
    .map((x) => `${x.year}-${String(x.sortKey % 100).padStart(2, '0')}`);

  // Ordina i file dentro ogni mese dal più recente al meno recente usando timestamp estratto
  Object.keys(grouped).forEach((label) => {
    grouped[label].files.sort((a, b) => {
      const ta = extractTimestampFromEnd(a) ?? 0;
      const tb = extractTimestampFromEnd(b) ?? 0;
      return tb - ta;
    });
  });

  return { grouped, orderedLabels };
}

export default function UploadList() {
  const selectedOrg = localStorage.getItem('selectedOrg');

  const [loading, setLoading] = useState(true);

  const [year, setYear] = useState(2026);

  // cache per anno: { [year]: { grouped, orderedLabels } }
  const [cacheByYear, setCacheByYear] = useState({});

  const [selectedMonthLabel, setSelectedMonthLabel] = useState(null);
  const [page, setPage] = useState(1);
  const [selectedImage, setSelectedImage] = useState(null);

  const [fileSearch, setFileSearch] = useState('');

  const fetchUploadsForYear = useCallback(
    async (targetYear) => {
      if (!selectedOrg || !targetYear) return;

      // cache hit
      if (cacheByYear[targetYear]) {
        setLoading(false);
        const labels = cacheByYear[targetYear].orderedLabels || [];
        if (!selectedMonthLabel || !cacheByYear[targetYear].grouped?.[selectedMonthLabel]) {
          setSelectedMonthLabel(labels[0] ?? null);
          setPage(1);
        }
        return;
      }

      setLoading(true);
      try {
        const res = await authFetch(
          `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/checklist/protected/uploads/${selectedOrg}/${targetYear}`,
          { method: 'GET', credentials: 'include' }
        );

        if (!res.ok) {
          setCacheByYear((prev) => ({
            ...prev,
            [targetYear]: { grouped: {}, orderedLabels: [] },
          }));
          setSelectedMonthLabel(null);
          setPage(1);
          return;
        }

        const data = await res.json().catch(() => []);
        const files = Array.isArray(data) ? data : [];

        const { grouped, orderedLabels } = groupFilesByMonth(files);

        // ⚠️ se per qualunque motivo l'endpoint "2026" ti torna file con timestamp di altri anni,
        // li escludo dalla sidebar/visualizzazione dell'anno selezionato:
        const filteredGrouped = {};
        orderedLabels.forEach((label) => {
          const block = grouped[label];
          if (block?.year === targetYear) filteredGrouped[label] = block;
        });

        const filteredLabels = Object.keys(filteredGrouped).sort((a, b) => b.localeCompare(a));

        setCacheByYear((prev) => ({
          ...prev,
          [targetYear]: { grouped: filteredGrouped, orderedLabels: filteredLabels },
        }));

        setSelectedMonthLabel(filteredLabels[0] ?? null);
        setPage(1);
      } catch (err) {
        console.error('Errore fetch:', err);
        setCacheByYear((prev) => ({
          ...prev,
          [targetYear]: { grouped: {}, orderedLabels: [] },
        }));
        setSelectedMonthLabel(null);
        setPage(1);
      } finally {
        setLoading(false);
      }
    },
    [selectedOrg, cacheByYear, selectedMonthLabel]
  );

  useEffect(() => {
    fetchUploadsForYear(year);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, selectedOrg]);

  const yearData = cacheByYear[year] || { grouped: {}, orderedLabels: [] };
  const uploadsByMonth = yearData.grouped;

  const currentFiles = useMemo(() => {
    const files =
      selectedMonthLabel && uploadsByMonth[selectedMonthLabel]
        ? uploadsByMonth[selectedMonthLabel].files
        : [];

    const term = fileSearch.trim().toLowerCase();
    if (!term) return files;

    return files.filter((f) => f.toLowerCase().includes(term));
  }, [selectedMonthLabel, uploadsByMonth, fileSearch]);

  const totalPages = Math.max(1, Math.ceil(currentFiles.length / IMAGES_PER_PAGE));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const paginated = useMemo(() => {
    const start = (page - 1) * IMAGES_PER_PAGE;
    return currentFiles.slice(start, start + IMAGES_PER_PAGE);
  }, [currentFiles, page]);

  return (
    <Box>
      <Typography level="h3" mb={2}>
        Archivio Immagini Checklist
      </Typography>

      <Box display="flex" gap={3} flexWrap="wrap">
        {/* Sidebar */}
        <Sheet
          variant="outlined"
          sx={{
            width: { xs: '100%', sm: 220 },
            borderRadius: 'lg',
            p: 1.5,
            flexShrink: 0,
          }}
        >
        <Typography level="title-sm" mb={1}>
          Anno
        </Typography>

        <Sheet
          variant="soft"
          sx={{
            p: 1,
            borderRadius: 'md',
            mb: 1.5,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(2, 1fr)' },
              gap: 1,
            }}
          >
            {anniDisponibili.map((y) => (
              <Button
                key={y}
                size="sm"
                variant={y === year ? 'solid' : 'soft'}
                color={y === year ? 'primary' : 'neutral'}
                onClick={() => {
                  setYear(y);
                  setFileSearch('');
                  setPage(1);
                }}
                sx={{
                  minHeight: 34,
                  fontWeight: 'lg',
                  borderRadius: 'md',
                }}
              >
                {y}
              </Button>
            ))}
          </Box>

          <Typography
            level="body-xs"
            color="neutral"
            sx={{ mt: 1, textAlign: 'center' }}
          >
            Seleziona l’anno per filtrare i caricamenti
          </Typography>
        </Sheet>

          <Divider sx={{ my: 1 }} />

          <Typography level="title-sm" mb={1}>
            Mese
          </Typography>

          {loading ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size="sm" />
            </Box>
          ) : yearData.orderedLabels.length === 0 ? (
            <Typography level="body-sm" color="neutral">
              Nessun upload per {year}.
            </Typography>
          ) : (
            <List size="sm">
              {yearData.orderedLabels.map((label) => {
                const item = uploadsByMonth[label];
                if (!item) return null;

                return (
                  <ListItem key={label} sx={{ py: 0.25, px: 0 }}>
                    <Button
                      size="sm"
                      fullWidth
                      variant={label === selectedMonthLabel ? 'solid' : 'soft'}
                      onClick={() => {
                        setSelectedMonthLabel(label);
                        setPage(1);
                      }}
                      sx={{
                        fontSize: '0.8rem',
                        textTransform: 'capitalize',
                        minHeight: 30,
                        justifyContent: 'space-between',
                        px: 1,
                      }}
                    >
                      <span>{item.month}</span>
                        {item.files.length}
                    </Button>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Sheet>

        {/* Contenuto */}
        <Box flex={1} minWidth={280}>
          <Box
            display="flex"
            gap={1.5}
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            mb={2}
          >
            <Input
              placeholder="Filtra per nome file..."
              value={fileSearch}
              onChange={(e) => {
                setFileSearch(e.target.value);
                setPage(1);
              }}
              sx={{ width: { xs: '100%', sm: 360 } }}
              endDecorator={
                fileSearch ? (
                  <IconButton
                    variant="plain"
                    color="neutral"
                    onClick={() => {
                      setFileSearch('');
                      setPage(1);
                    }}
                  >
                    <X size={16} />
                  </IconButton>
                ) : null
              }
            />

            <Typography level="body-sm" color="neutral">
              {currentFiles.length} immagini
              {selectedMonthLabel ? ` • ${selectedMonthLabel}` : ''}
            </Typography>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" mt={4}>
              <CircularProgress />
            </Box>
          ) : paginated.length > 0 ? (
            <>
              <Box
                display="grid"
                gridTemplateColumns="repeat(auto-fill, minmax(160px, 1fr))"
                gap={2}
              >
                {paginated.map((file) => {
                  const imageUrl = `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/checklist/protected/uploads/${selectedOrg}/${year}/${file}`;

                  return (
                    <Sheet
                      key={file}
                      variant="outlined"
                      sx={{
                        borderRadius: 'md',
                        p: 0.5,
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover .download-icon': { opacity: 1 },
                      }}
                    >
                      <IconButton
                        component="a"
                        href={imageUrl}
                        download
                        size="sm"
                        color="neutral"
                        variant="solid"
                        className="download-icon"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 2,
                          opacity: 0,
                          transition: 'opacity 0.2s',
                          backgroundColor: 'rgba(0, 0, 0, 0.6)',
                          color: 'white',
                          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.8)' },
                        }}
                        title="Scarica"
                      >
                        <Download size={16} />
                      </IconButton>

                      <AspectRatio
                        ratio="1"
                        sx={{ borderRadius: 'sm', cursor: 'pointer', width: '100%' }}
                        onClick={() => setSelectedImage(imageUrl)}
                      >
                        <img src={imageUrl} alt={file} loading="lazy" />
                      </AspectRatio>

                      <Typography level="body-xs" sx={{ mt: 0.5 }} noWrap title={file}>
                        {file}
                      </Typography>
                    </Sheet>
                  );
                })}
              </Box>

              {totalPages > 1 && (
                <Box mt={3} display="flex" justifyContent="center" gap={2} alignItems="center">
                  <Button
                    variant="outlined"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Precedente
                  </Button>
                  <Typography level="body-sm">
                    Pagina {page} di {totalPages}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="sm"
                    disabled={page === totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Successiva
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <Typography level="body-md" color="neutral" mt={2}>
              Nessuna immagine per il periodo selezionato.
            </Typography>
          )}
        </Box>
      </Box>

      <Modal open={!!selectedImage} onClose={() => setSelectedImage(null)}>
        <ModalDialog layout="center" size="lg">
          <ModalClose />
          <Typography level="title-lg" mb={1}>
            Anteprima
          </Typography>
          <Box display="flex" justifyContent="center" mb={2}>
            <img
              src={selectedImage}
              alt="Anteprima"
              style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8 }}
            />
          </Box>
          <Button
            component="a"
            href={selectedImage}
            download
            fullWidth
            startDecorator={<Download size={16} />}
          >
            Scarica
          </Button>
        </ModalDialog>
      </Modal>
    </Box>
  );
}